/**
 * VerificationRequestManager - Handles incoming device verification requests
 * 
 * Key Features:
 * - Listens for incoming verification requests
 * - Checks if current device is verified before showing toast (CRITICAL!)
 * - Manages toast notifications for verification requests
 * - Tracks active requests
 * - Handles timeouts
 * - Coordinates toast → modal handoff
 */

import deviceTrustManager from '../crypto/DeviceTrustManager';
import logger from '../utils/logger';

const VERIFICATION_TIMEOUT = 5 * 60 * 1000; // 5 minutes

class VerificationRequestManager {
  constructor() {
    this.activeRequests = new Map(); // requestId -> request object
    this.toastContext = null; // Will be set by initialization
    this.modalContext = null; // Will be set by initialization
    this.matrixService = null; // Will be set by initialization
  }

  /**
   * Initialize manager with required contexts
   * Should be called after app contexts are available
   */
  initialize(toastContext, modalContext, matrixService) {
    this.toastContext = toastContext;
    this.modalContext = modalContext;
    this.matrixService = matrixService;
    logger.debug('VerificationRequestManager: Initialized');
  }

  /**
   * Handle incoming verification request
   * This is the CRITICAL method that implements the "only show toast on verified devices" logic
   * 
   * @param {Object} request - Matrix verification request object
   */
  async handleIncomingRequest(request) {
    if (!request) {
      logger.warn('VerificationRequestManager: No request provided');
      return;
    }

    const requestId = request.transactionId || request.channel?.transactionId;
    if (!requestId) {
      logger.warn('VerificationRequestManager: No transaction ID in request');
      return;
    }

    logger.debug('VerificationRequestManager: Received verification request:', requestId);

    // CRITICAL: Check if current device is verified before showing toast
    const isCurrentDeviceVerified = await deviceTrustManager.isCurrentDeviceVerified();

    if (!isCurrentDeviceVerified) {
      logger.debug('VerificationRequestManager: Ignoring request - current device is not verified');
      return; // Don't show toast on unverified devices
    }

    // Check if request is from same user (device verification)
    const client = this.matrixService?.client;
    if (!client) {
      logger.warn('VerificationRequestManager: No Matrix client available');
      return;
    }

    const currentUserId = client.getUserId();
    const requestUserId = request.otherUserId || request.userId;

    if (requestUserId !== currentUserId) {
      // This is a user verification request, not device verification
      // For now, we only handle device verification via toast
      logger.debug('VerificationRequestManager: Request is for user verification, not device');
      return;
    }

    // Check if we already have this request
    if (this.activeRequests.has(requestId)) {
      logger.debug('VerificationRequestManager: Request already being handled:', requestId);
      return;
    }

    // Store the request
    this.activeRequests.set(requestId, request);

    // Get device name for the requesting device
    const deviceName = await this.getDeviceName(request);

    // Show toast notification
    if (!this.toastContext) {
      logger.warn('VerificationRequestManager: Toast context not initialized');
      return;
    }

    this.toastContext.showVerificationRequest({
      requestId,
      deviceName,
      onVerify: () => this.handleVerifyClick(request, requestId),
      onIgnore: () => this.handleIgnoreClick(requestId),
      timeout: VERIFICATION_TIMEOUT,
    });

    logger.info(`Received verification request from device: ${deviceName}`);
  }

  /**
   * Get display name for the device making the request
   */
  async getDeviceName(request) {
    try {
      const client = this.matrixService?.client;
      if (!client) {
        return 'Unknown Device';
      }

      const crypto = client.getCrypto?.();
      if (!crypto) {
        return 'Unknown Device';
      }

      const userId = request.otherUserId || request.userId;
      const deviceId = request.otherDeviceId || request.deviceId;

      if (!userId || !deviceId) {
        return 'Unknown Device';
      }

      // Try to get device info using correct Rust crypto API
      const userDevices = await crypto.getUserDeviceInfo?.([userId]);
      const device = userDevices?.get?.(userId)?.get?.(deviceId);
      
      if (device?.display_name || device?.displayName) {
        return device.display_name || device.displayName;
      }

      // Fallback to device ID
      return `Device ${deviceId.substring(0, 8)}`;
    } catch (error) {
      logger.warn('VerificationRequestManager: Could not get device name:', error);
      return 'Unknown Device';
    }
  }

  /**
   * Handle user clicking "Verify" in toast
   * Opens verification modal - actual modal rendering should be handled by app component
   */
  handleVerifyClick(request, requestId) {
    logger.info('User clicked Verify button for request:', requestId);

    // Emit event for modal to open
    // The app component will listen for this and open the modal
    if (this.onVerifyRequest) {
      try {
        const result = this.onVerifyRequest(request, requestId);
        if (result && result.then) {
          result.catch((err) => {
            logger.error('Error in onVerifyRequest callback:', err);
          });
        }
      } catch (err) {
        logger.error('Error executing onVerifyRequest callback:', err);
      }
    } else {
      logger.warn('VerificationRequestManager: No onVerifyRequest callback set');
    }
  }

  /**
   * Set callback for when user clicks Verify
   * @param {Function} callback - Function(request, requestId)
   */
  setOnVerifyRequest(callback) {
    this.onVerifyRequest = callback;
  }

  /**
   * Handle user clicking "Ignore" in toast
   */
  handleIgnoreClick(requestId) {
    logger.debug('VerificationRequestManager: User clicked Ignore:', requestId);

    // Just remove from active requests, don't cancel the request server-side
    // The request will timeout naturally
    this.activeRequests.delete(requestId);
  }

  /**
   * Handle request timeout
   */
  handleTimeout(requestId) {
    logger.debug('VerificationRequestManager: Request timeout:', requestId);

    const request = this.activeRequests.get(requestId);
    if (request) {
      try {
        // Cancel the request server-side
        request.cancel?.('timeout');
      } catch (error) {
        logger.warn('VerificationRequestManager: Error cancelling request:', error);
      }
      
      this.activeRequests.delete(requestId);
    }
  }

  /**
   * Clear all active requests
   */
  clearAll() {
    this.activeRequests.clear();
  }

  /**
   * Get number of active requests
   */
  getActiveRequestCount() {
    return this.activeRequests.size;
  }
}

// Create singleton instance
const verificationRequestManager = new VerificationRequestManager();
export default verificationRequestManager;

// Also export class for testing
export { VerificationRequestManager };
